import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleSelect } from '../BubbleUI';
import {
  BRAND_FORM_PREFERENCES_NAME,
  Brand,
  BrandCommonTextValues,
  BrandFormFieldConfig,
  BrandFormFieldName,
  BrandFormFieldPreference,
  BrandFormName,
} from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { getCommonTexts } from '@go-shared/lib/brand';
import { createPortal } from 'react-dom';
import { GROUP_PARTY_FIELD_NAMES } from '@go-shared/types/groupParty';
import { notUndefined } from '@go-shared/lib/functional';

const MANDATORY_OPTIONS = ['optional', 'mandatory'] as const;
type MandatoryOption = typeof MANDATORY_OPTIONS[number];

const MANDATORY_OPTION_LABELS: Record<MandatoryOption, string> = {
  optional: 'Optional',
  mandatory: 'Mandatory',
};

function toMandatoryOption(mandatory: boolean): MandatoryOption {
  return mandatory ? 'mandatory' : 'optional';
}

interface FormFieldConfigDefinition {
  title: string;
  fieldNames: readonly BrandFormFieldName[];
  getFieldLabels: (
    commonTextValues: BrandCommonTextValues
  ) => Partial<Record<BrandFormFieldName, string>>;
}

const FORM_FIELD_CONFIG_DEFINITIONS: Record<BrandFormName, FormFieldConfigDefinition> = {
  group_party: {
    title: 'Group Details Form',
    fieldNames: GROUP_PARTY_FIELD_NAMES,
    getFieldLabels: commonTextValues => ({
      groupId: 'Group ID',
      groupType: 'Group Type',
      contact: commonTextValues.group_contact_name,
      size: commonTextValues.guests,
      groupGuideName: commonTextValues.tour_leader,
      groupGuideCabin: commonTextValues.tour_leader_cabin,
      bookingNo: 'Booking No.',
      salesOffice: 'Agency',
      agencyContactName: commonTextValues.agency_contact_name,
      salesEmail: commonTextValues.sales_dept_email,
      salesPhone: commonTextValues.sales_dept_phone,
      embark: commonTextValues.embark_date,
      debark: commonTextValues.debark_date,
      cabinDrops: 'Cabin Drop Requests',
      diningPreference: 'Dining Preference',
      prepaidPackages: 'Pre-Paid Packages',
      guestExperience: commonTextValues.guest_experience,
      notes: 'Group Notes',
      hospitalityDesk: commonTextValues.hospitality_desk,
      balanceDue: 'Balance Due',
      paymentStatus: 'Payment Status',
    }),
  },
};

interface BrandFieldConfigFormDataBase {
  formsFields: Record<BrandFormName, BrandFormFieldPreference[]>;
}

type BrandFieldConfigFormData = BrandFieldConfigFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandFieldConfigFormDataBase]: string };

function DEFAULT_FORM_DATA(brand: Brand): BrandFieldConfigFormData {
  const configs = BRAND_FORM_PREFERENCES_NAME.map(formName =>
    brand.formConfig.find(config => config.formName === formName)
  ).filter(notUndefined);

  const formsFields = Object.fromEntries(
    configs.map(config => [config.formName, config.fields])
  ) as Record<BrandFormName, BrandFormFieldPreference[]>;

  return {
    formsFields,
    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandFieldConfigForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandFieldConfigFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandFieldConfigFormDataBase>(
    key: T,
    val: BrandFieldConfigFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  function setMandatory(
    formName: BrandFormName,
    fieldName: BrandFormFieldName,
    mandatory: boolean
  ) {
    changeForm('formsFields', {
      ...formData.formsFields,
      [formName]: formData.formsFields[formName].map(field =>
        field.fieldName === fieldName ? { ...field, mandatory } : field
      ),
    });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        const formConfig: BrandFormFieldConfig[] = BRAND_FORM_PREFERENCES_NAME.map(formName => ({
          formName,
          fields: formData.formsFields[formName],
        }));

        await updateBrand(props.edit._id, {
          ...props.edit,
          formConfig,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const commonTextValues = getCommonTexts(
    { default: props.edit.commonTexts, translations: [] },
    { selected: 'en' }
  );

  const formHtml = (
    <div>
      {BRAND_FORM_PREFERENCES_NAME.map(formName => {
        const definition = FORM_FIELD_CONFIG_DEFINITIONS[formName];
        const fieldLabels = definition.getFieldLabels(commonTextValues);

        return (
          <div key={formName} className="mb-6">
            <h2 className="text-lg text-navy font-semibold mb-4">{definition.title}</h2>
            <div className="grid grid-cols-2 gap-4 items-center">
              {formData.formsFields[formName].map(field => (
                <BubbleFormGroup label={fieldLabels[field.fieldName]} key={field.fieldName}>
                  <BubbleSelect
                    block
                    allowUndefined={false}
                    options={[...MANDATORY_OPTIONS]}
                    labelExtractor={opt => MANDATORY_OPTION_LABELS[opt]}
                    value={toMandatoryOption(field.mandatory)}
                    onValue={opt => setMandatory(formName, field.fieldName, opt === 'mandatory')}
                  />
                </BubbleFormGroup>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
